import React, { useEffect, useState } from 'react';
import {
  AlertTriangle, Building2, CheckCircle, ChevronDown, ChevronUp, Clock,
  CreditCard, Edit3, FileText, Mail, Package, Phone, Plus, Save, Settings,
  Stethoscope, UserCheck, UserCog, Users, X, XCircle
} from 'lucide-react';
import { apiClient as api } from '../../lib/api';
import { toast } from '../../lib/toast';
import { useUser } from '../../store/useAuthStore';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';
import CommissionOverridesPanel from '../commissionOverrides/CommissionOverridesPanel';
import ClinicDealsPanel from '../clinicDeals/ClinicDealsPanel';
import { DtpAutoImportSection } from './DtpAutoImportSection';
import { TelemedNetworkSection } from './TelemedNetworkSection';
import { AdminNpiEditor } from './AdminNpiEditor';
import { UserChainPanel, UserStateBanner } from './UserChainPanel';
import { PartnerApiSection } from './PartnerApiSection';
import PhysicianIDProofingViewer from '../physicians/PhysicianIDProofingViewer';
import { AvaAccessToggle } from '../ava/AvaAccessToggle';

/**
 * The account detail modal, shared by the role-scoped pages and All Users.
 *
 * Lifted out of UserManagement.tsx, where it was a 1,449-line inline block
 * wired to ~60 pieces of that page's state. All of that state was modal-scoped,
 * so it moved with the markup; only the things that drive OTHER modals
 * (impersonate, move-supervisor, reassign confirmation) stay the parent's
 * business and arrive as callbacks.
 *
 * It owns its own loading — hand it a user and it fetches whatever that role
 * needs. That is what lets All Users open the same modal without reproducing
 * the page's state.
 */
/** The page's UserDisplay interface is not exported; the modal only reads it. */
type UserDisplay = any;

export interface UserAccessModalProps {
  /** The account being inspected. */
  subject: any;
  onClose: () => void;
  /** Merge a patch into the subject (percent-markup and sales-rep toggles). */
  onSubjectPatched?: (patch: Record<string, unknown>) => void;
  /** Open the outer move-supervisor modal for an AM. */
  onMoveSupervisor?: () => void;
  /** Open the outer impersonation modal. */
  onImpersonate?: () => void;
  /** An AM was picked for reassignment; the parent confirms it. */
  onSelectNewAM?: (am: any) => void;
  /** Open another account (the AM or supervisor) from the hierarchy. */
  onOpenUser?: (id: string) => void;
  /** Bump to force a refetch while the modal stays open (e.g. after a reassign). */
  refreshToken?: number;
}


/**
 * What the account told us when it signed up.
 *
 * Register.tsx asks two optional intake questions — "How did you hear about
 * us?" and "What products and volumes are you interested in?" — and people
 * genuinely answer them. Neither was rendered anywhere, so approvals were being
 * decided from a name, an email and a company while the one piece of context
 * that says whether someone is real sat unread on the record.
 *
 * Reads straight off the subject; no extra request.
 */
function SignupIntake({ subject }: { subject: any }) {
  const method = subject?.registrationMethod;
  // referralSource arrives with stray leading whitespace on some records.
  const referral = (subject?.referralSource || '').trim();
  const interest = (subject?.productInterest || '').trim();
  const inviter = subject?.invitedById;
  const inviterName = inviter && typeof inviter === 'object'
    ? [inviter.profile?.firstName, inviter.profile?.lastName].filter(Boolean).join(' ') || inviter.email
    : null;

  const how = method === 'invitation'
    ? `Invited${inviterName ? ` by ${inviterName}` : ''}`
    : method === 'admin_created' ? 'Created by an admin'
    : method === 'self_serve' ? 'Signed up via web form'
    : null;

  // Nothing recorded at all — usually an older account predating the intake
  // questions. Showing an empty panel would just be noise.
  if (!how && !referral && !interest) return null;

  const fmt = (d?: string) => d
    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-4">
      <h3 className="font-medium text-gray-900 mb-3 flex items-center">
        <FileText className="w-5 h-5 mr-2 text-blue-600" />
        Signup intake
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">How they joined</div>
          <div className="text-gray-900 mt-0.5">{how || '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Registered</div>
          <div className="text-gray-900 mt-0.5">
            {fmt(subject?.createdAt) || '—'}
            {!subject?.lastLoginAt && (
              // Signed up and never came back — worth knowing before chasing.
              <span className="ml-2 text-xs text-amber-700">never signed in since</span>
            )}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs text-gray-500 uppercase tracking-wide">How did you hear about us?</div>
          <div className="text-gray-900 mt-0.5">{referral || <span className="text-gray-400">Not answered</span>}</div>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Products and volumes they want</div>
          <div className="text-gray-900 mt-0.5 whitespace-pre-wrap">
            {interest || <span className="text-gray-400">Not answered</span>}
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Network assignment, for a clinic that registered with no NPI and asked for
 * the doctor network.
 *
 * Two things made this worth surfacing at the top rather than leaving it to the
 * Telemed network section further down.
 *
 * It is a TWO-step action and the second half is easy to miss. Picking a
 * network without ticking "telemed-only" leaves the clinic looking assigned
 * while still having no prescriber, and `telemedNetworkRequested` only clears
 * when both are set — so the queue keeps listing them and nobody knows why.
 *
 * And completing it can silently REVOKE verification. The `isVerified` virtual
 * swaps its second gate when telemedOnly is set: a normal clinic verifies on
 * `credentialVerification`, a telemed-only one on the signed Contract + BAA.
 * In production all 22 accounts waiting on a network are unsigned and 13 are
 * currently verified on a credential, so finishing the assignment would drop
 * every one of them out of verified without saying so.
 */
function NetworkAssignment({ subject }: { subject: any }) {
  const pd = subject?.physicianData || {};
  const requested = pd.telemedNetworkRequested === true;
  const assigned = !!pd.telemedNetworkId;
  const telemedOnly = pd.telemedOnly === true;

  // Only while the request is outstanding; once both steps are done the
  // Telemed network section below tells the whole story.
  if (!requested || (assigned && telemedOnly)) return null;

  const agreement = pd.idProofingData || {};
  const signed = agreement.agreementAccepted === true;
  const idApproved = pd.idProofingStatus === 'approved';
  const credVerified = pd.credentialVerification?.status === 'verified';
  const wouldLoseVerification = idApproved && credVerified && !signed;

  const fmt = (d?: string) => d
    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  const Step = ({ done, n, children }: { done: boolean; n: number; children: React.ReactNode }) => (
    <li className="flex items-start gap-2">
      <span className={`mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center flex-shrink-0 ${
        done ? 'bg-green-600 text-white' : 'bg-gray-300 text-white'
      }`}>{done ? '\u2713' : n}</span>
      <span className={done ? 'text-gray-500 line-through' : 'text-gray-900'}>{children}</span>
    </li>
  );

  return (
    <div className="border border-orange-200 bg-orange-50/50 rounded-lg p-4 mb-4">
      <h3 className="font-medium text-gray-900 mb-1 flex items-center">
        <Stethoscope className="w-5 h-5 mr-2 text-orange-600" />
        Needs a doctor network
      </h3>
      <p className="text-xs text-gray-600 mb-3">
        Registered without an NPI and asked for the network. It cannot prescribe until both steps are done.
      </p>

      <ol className="space-y-1.5 text-sm mb-3">
        <Step done={assigned} n={1}>Assign a telemed network</Step>
        <Step done={telemedOnly} n={2}>
          Tick “Telemed-only clinic (no NPI)” — without this it still has no prescriber
        </Step>
      </ol>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm border-t border-orange-200 pt-3">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Contract &amp; BAA</div>
          <div className="mt-0.5">
            {signed ? (
              <span className="text-green-800">
                Signed{agreement.agreementType === 'telemed_contract_baa' ? ' (telemed contract + BAA)' : ' (standard terms)'}
                {agreement.agreementVersion ? ` · v${agreement.agreementVersion}` : ''}
                {fmt(agreement.agreementAcceptedAt) ? ` · ${fmt(agreement.agreementAcceptedAt)}` : ''}
              </span>
            ) : <span className="text-amber-800">Not signed</span>}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">NPI on file</div>
          <div className="mt-0.5 text-gray-700">
            {pd.npiNumber || <span className="text-gray-500">None — expected for telemed-only</span>}
          </div>
        </div>
      </div>

      {wouldLoseVerification && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-100 border border-amber-300 p-2.5 text-xs text-amber-900">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            <strong>This account is currently verified on its credential.</strong> Marking it
            telemed-only switches verification to the signed Contract &amp; BAA, which it does not
            have — so it will stop being verified. Get the agreement signed first, or expect to
            re-verify afterwards.
          </span>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-3">
        Assign it in the <strong>Telemed network</strong> section below.
      </p>
    </div>
  );
}

export function UserAccessModal({
  subject,
  onClose,
  onSubjectPatched = () => {},
  onMoveSupervisor = () => {},
  onImpersonate = () => {},
  onSelectNewAM = () => {},
  onOpenUser,
  refreshToken = 0
}: UserAccessModalProps) {
  const user = useUser();
  const [showIdProofing, setShowIdProofing] = useState(false);
  useEffect(() => { setShowIdProofing(false); }, [subject?._id]);

  /**
   * The moved code updated the selected user in place via setSelectedUser.
   * The subject is a prop here, so those writes are forwarded to the parent
   * instead — supporting both the object and updater-function forms the
   * original used.
   */
  const setSelectedUser = (updater: any) => {
    const next = typeof updater === 'function' ? updater(subject) : updater;
    if (next) onSubjectPatched(next);
  };

  const [accessModalData, setAccessModalData] = useState<any | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [paymentInstructions, setPaymentInstructions] = useState<string>('');
  const [originalPaymentInstructions, setOriginalPaymentInstructions] = useState<string>('');
  const [editingPaymentInstructions, setEditingPaymentInstructions] = useState(false);

  // Percent-markup mode admin controls (supervisor / AM only)
  const [percentMarkupSaving, setPercentMarkupSaving] = useState(false);
  const [percentMarkupInput, setPercentMarkupInput] = useState<string>('');
  const [percentMarkupError, setPercentMarkupError] = useState<string | null>(null);
  const [percentMarkupOffending, setPercentMarkupOffending] = useState<any[] | null>(null);
  const [salesRepSaving, setSalesRepSaving] = useState(false);
  const [salesRepError, setSalesRepError] = useState<string | null>(null);

  // Modal: collapsible sections and downstream user lists
  const [amPhysicians, setAmPhysicians] = useState<any[]>([]);
  const [supervisorAMs, setSupervisorAMs] = useState<any[]>([]);
  const [supervisorPhysicians, setSupervisorPhysicians] = useState<any[]>([]);
  const [showPhysicians, setShowPhysicians] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [showSupervisorAMs, setShowSupervisorAMs] = useState(false);
  const [showSupervisorPhysicians, setShowSupervisorPhysicians] = useState(false);

  // Team member state (supervisor multi-login)
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({ email: '', firstName: '', lastName: '' });
  const [addTeamMemberLoading, setAddTeamMemberLoading] = useState(false);
  const [savingPaymentInstructions, setSavingPaymentInstructions] = useState(false);
  
  // Physician reassignment state
  const [showReassignDropdown, setShowReassignDropdown] = useState(false);
  const [availableAccountManagers, setAvailableAccountManagers] = useState<UserDisplay[]>([]);
  const [loadingAccountManagers, setLoadingAccountManagers] = useState(false);
  // Set when the search has more matches than were loaded.
  const [accountManagersTotal, setAccountManagersTotal] = useState<number | null>(null);
  const amSearchSeq = React.useRef(0);
  const [searchAM, setSearchAM] = useState('');
  // Billing configuration state (for physicians)
  const [editingBillingConfig, setEditingBillingConfig] = useState(false);
  const [billingConfigLoading, setBillingConfigLoading] = useState(false);
  const [billingCreditLimit, setBillingCreditLimit] = useState<string>('');
  const [billingPaymentTerms, setBillingPaymentTerms] = useState<'immediate' | 'net7' | 'net15' | 'net30'>('immediate');
  const [billingArEnabled, setBillingArEnabled] = useState<boolean>(false);
  const [billingPatientPayEnabled, setBillingPatientPayEnabled] = useState<boolean>(false);
  const [billingFacilityOnly, setBillingFacilityOnly] = useState<boolean>(false);
  const [billingDtpEnabled, setBillingDtpEnabled] = useState<boolean>(false);
  const [billingCustomDomainEnabled, setBillingCustomDomainEnabled] = useState(false);
  const [billingMembershipEnabled, setBillingMembershipEnabled] = useState<boolean>(false);
  const [billingVolumeRewardsEnabled, setBillingVolumeRewardsEnabled] = useState<boolean>(false);
  const [billingIsPharmacy, setBillingIsPharmacy] = useState<boolean>(false);
  // Closed by default: reference, not the reason anyone opened this modal.
  const [flagsOpen, setFlagsOpen] = useState(false);
  const [creditStatus, setCreditStatus] = useState<{
    creditLimit: number | null;
    outstandingBalance: number;
    availableCredit: number | null;
    isOverLimit: boolean;
    invoiceCount: number;
  } | null>(null);
  const [_creditStatusLoading, _setCreditStatusLoading] = useState(false);

  const handleEditPaymentInstructions = () => {
    setOriginalPaymentInstructions(paymentInstructions);
    setEditingPaymentInstructions(true);
  };

  const handleCancelEditPaymentInstructions = () => {
    setPaymentInstructions(originalPaymentInstructions);
    setEditingPaymentInstructions(false);
    setOriginalPaymentInstructions('');
  };

  const handleSavePaymentInstructions = async () => {
    if (!subject) return;

    setSavingPaymentInstructions(true);
    try {
      const result = await api.updatePaymentInstructionsAsAdmin(subject._id, paymentInstructions);
      if (result.success) {
        toast.success('Payment instructions updated successfully!');
        setEditingPaymentInstructions(false);
        setOriginalPaymentInstructions('');
      } else {
        toast.error(result.error || 'Failed to update payment instructions');
      }
    } catch (error: any) {
      console.error('Error updating payment instructions:', error);
      toast.error('Failed to update payment instructions');
    } finally {
      setSavingPaymentInstructions(false);
    }
  };

  const handleSaveBillingConfig = async () => {
    if (!subject) return;

    setBillingConfigLoading(true);
    try {
      const creditLimit = billingCreditLimit === '' ? null : parseFloat(billingCreditLimit);

      const result = await api.updatePhysicianBillingConfig(subject._id, {
        creditLimit,
        paymentTerms: billingPaymentTerms,
        arEnabled: billingArEnabled,
        patientPayEnabled: billingPatientPayEnabled,
        facilityOnly: billingFacilityOnly,
        directToPatientEnabled: billingDtpEnabled,
        membershipEnabled: billingMembershipEnabled,
        customDomainEnabled: billingCustomDomainEnabled,
        volumeRewardsEnabled: billingVolumeRewardsEnabled,
        isPharmacy: billingIsPharmacy
      });

      if (result.success) {
        toast.success('Billing configuration updated successfully!');
        setEditingBillingConfig(false);

        // Refresh credit status
        const creditRes = await api.getCreditStatus(subject._id);
        if (creditRes.success && creditRes.data) {
          setCreditStatus(creditRes.data);
        }

        // Update selected user in-memory to reflect changes
        setSelectedUser((prev: any) => prev ? {
          ...prev,
          physicianData: {
            ...prev.physicianData,
            creditLimit,
            paymentTerms: billingPaymentTerms,
            arEnabled: billingArEnabled,
            patientPayEnabled: billingPatientPayEnabled,
            facilityOnly: billingFacilityOnly,
            directToPatientEnabled: billingDtpEnabled,
                membershipEnabled: billingMembershipEnabled,
            customDomainEnabled: billingCustomDomainEnabled,
            volumeRewardsEnabled: billingVolumeRewardsEnabled,
            isPharmacy: billingIsPharmacy
          }
        } as UserDisplay : null);
      } else {
        toast.error(result.error || 'Failed to update billing configuration');
      }
    } catch (error: any) {
      console.error('Error updating billing configuration:', error);
      toast.error('Failed to update billing configuration');
    } finally {
      setBillingConfigLoading(false);
    }
  };

  const handleCancelBillingConfig = () => {
    // Reset to original values
    setBillingCreditLimit(subject?.physicianData?.creditLimit?.toString() || '');
    setBillingPaymentTerms(subject?.physicianData?.paymentTerms || 'immediate');
    setBillingArEnabled(subject?.physicianData?.arEnabled || false);
    setBillingPatientPayEnabled(subject?.physicianData?.patientPayEnabled || false);
    setBillingFacilityOnly(subject?.physicianData?.facilityOnly || false);
    // DTP defaults on — only explicit `false` means opted out
    setBillingDtpEnabled(subject?.physicianData?.directToPatientEnabled !== false);
    setBillingMembershipEnabled((subject?.physicianData as any)?.membershipEnabled === true);
    setBillingCustomDomainEnabled(subject?.physicianData?.customDomainEnabled === true);
    setBillingVolumeRewardsEnabled((subject?.physicianData as any)?.volumeRewardsEnabled === true);
    setBillingIsPharmacy((subject?.physicianData as any)?.isPharmacy === true);
    setEditingBillingConfig(false);
  };


  // Everyone under a parent (an AM's clinics, a supervisor's AMs), walking
  // every page: one 500-row page silently dropped the rest and the section
  // counts were wrong. A failure leaves that section empty, as before.
  const loadNetwork = (params: { role: 'distributor' | 'physician'; distributorId?: string; supervisorId?: string }): Promise<any[]> =>
    api.getAllUsers(params).catch(err => {
      console.error(`Failed to load ${params.role} network:`, err);
      return [];
    });

  const load = async (userItem: UserDisplay) => {
    setSelectedUser(userItem);
    setModalLoading(true);
    setModalError(null);
    setAccessModalData(null);
    setAmPhysicians([]);
    setSupervisorAMs([]);
    setSupervisorPhysicians([]);
    setShowPhysicians(false);
    setShowProducts(false);
    setShowSupervisorAMs(false);
    setShowSupervisorPhysicians(false);

    try {
      if (user?.role === 'admin') {
        if (userItem.role === 'distributor') {
          // Fetch pricing, payment instructions, and physicians for distributors
          const [pricingRes, paymentRes, physiciansRes] = await Promise.all([
            api.adminViewDistributorPricing(userItem._id),
            api.getPaymentInstructions(userItem._id),
            loadNetwork({ role: 'physician', distributorId: userItem._id })
          ]);

          setAccessModalData(pricingRes.data);
          setAmPhysicians(physiciansRes);
          setPaymentInstructions(paymentRes.data?.paymentInstructions || '');
          setEditingPaymentInstructions(false);
          setOriginalPaymentInstructions('');
        } else if (userItem.role === 'physician' || userItem.role === 'physician_readonly') {
          // Fetch pricing and credit status in parallel
          const [pricingRes, creditRes] = await Promise.all([
            api.adminViewPhysicianPricing(userItem._id),
            api.getCreditStatus(userItem._id)
          ]);

          if (pricingRes.success) {
            setAccessModalData(pricingRes.data);
          } else {
            // Handle API failure - if it's because physician is not assigned to a distributor, show empty state
            if (pricingRes.error?.includes('not assigned to a distributor') ||
                pricingRes.error?.includes('Physician not found') ||
                pricingRes.error?.includes('No catalogs assigned')) {
              setAccessModalData({ products: [], catalogs: [] }); // Empty state instead of error
            } else {
              throw new Error(pricingRes.error || 'Failed to fetch clinic pricing');
            }
          }

          // Set credit status and billing config
          if (creditRes.success && creditRes.data) {
            setCreditStatus(creditRes.data);
          }

          // Initialize billing config from physician data
          setBillingCreditLimit(userItem.physicianData?.creditLimit?.toString() || '');
          setBillingPaymentTerms(userItem.physicianData?.paymentTerms || 'immediate');
          setBillingArEnabled(userItem.physicianData?.arEnabled || false);
          setBillingPatientPayEnabled(userItem.physicianData?.patientPayEnabled || false);
          setBillingFacilityOnly(userItem.physicianData?.facilityOnly || false);
          // DTP defaults on — only explicit `false` means opted out
          setBillingDtpEnabled(userItem.physicianData?.directToPatientEnabled !== false);
          setBillingMembershipEnabled((userItem.physicianData as any)?.membershipEnabled === true);
          setBillingCustomDomainEnabled(userItem.physicianData?.customDomainEnabled === true);
          setBillingVolumeRewardsEnabled((userItem.physicianData as any)?.volumeRewardsEnabled === true);
          setBillingIsPharmacy((userItem.physicianData as any)?.isPharmacy === true);
          setEditingBillingConfig(false);
        } else if (userItem.role === 'supervisor') {
          // Fetch team members, AMs, physicians, and pricing for supervisor
          setTeamMembersLoading(true);
          try {
            const [tmRes, amsRes, pricingRes] = await Promise.all([
              api.getTeamMembers(userItem._id),
              loadNetwork({ role: 'distributor', supervisorId: userItem._id }),
              api.adminViewSupervisorPricing(userItem._id)
            ]);
            if (tmRes.success) {
              setTeamMembers(tmRes.data || []);
            }
            const ams = amsRes;
            setSupervisorAMs(ams);

            if (pricingRes.success && pricingRes.data) {
              setAccessModalData(pricingRes.data);
            } else {
              setAccessModalData({ products: [], catalogs: [] });
            }

            // Fetch physicians for all AMs under this supervisor
            if (ams.length > 0) {
              const physicianResults = await Promise.all(
                ams.map((am: any) => loadNetwork({ role: 'physician', distributorId: String(am._id) }))
              );
              setSupervisorPhysicians(physicianResults.flat());
            }
          } finally {
            setTeamMembersLoading(false);
          }
        } else {
          // For other roles, just show basic info
          setAccessModalData({});
        }
      } else if (user?.role === 'supervisor') {
        // Supervisors viewing AMs/physicians in their tree mirror the admin
        // flow (the same /admin/users/:id/pricing endpoints accept supervisor
        // now, scoped server-side to their own network).
        if (userItem.role === 'distributor') {
          const [pricingRes, physiciansRes] = await Promise.all([
            api.adminViewDistributorPricing(userItem._id),
            loadNetwork({ role: 'physician', distributorId: userItem._id })
          ]);
          if (pricingRes.success) {
            setAccessModalData(pricingRes.data);
          } else {
            setAccessModalData({ products: [], catalogs: [] });
          }
          setAmPhysicians(physiciansRes);
        } else if (userItem.role === 'physician' || userItem.role === 'physician_readonly') {
          const pricingRes = await api.adminViewPhysicianPricing(userItem._id);
          if (pricingRes.success) {
            setAccessModalData(pricingRes.data);
          } else if (
            pricingRes.error?.includes('not assigned to a distributor') ||
            pricingRes.error?.includes('Physician not found') ||
            pricingRes.error?.includes('No catalogs assigned')
          ) {
            setAccessModalData({ products: [], catalogs: [] });
          } else {
            throw new Error(pricingRes.error || 'Failed to fetch clinic pricing');
          }
        } else {
          setAccessModalData({});
        }
      } else if (user?.role === 'distributor') {
        // For distributors viewing their physicians
        if (userItem.role === 'physician' || userItem.role === 'physician_readonly') {
          try {
            const res = await api.getPhysicianPricing(userItem._id);
            if (res.success && res.data) {
              // Transform the response to match expected format
              // The AM viewing is the current user (themselves)
              setAccessModalData({
                products: res.data.products || [],
                catalogs: res.data.catalog ? [{
                  catalogId: res.data.catalog._id,
                  catalogName: res.data.catalog.name,
                  parentCatalogName: res.data.catalog.parentCatalogName
                }] : [],
                accountManager: {
                  id: user._id,
                  name: `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || user.email,
                  email: user.email
                }
              });
            } else {
              // Show empty state if no data, but still show AM info
              setAccessModalData({
                products: [],
                catalogs: [],
                accountManager: {
                  id: user._id,
                  name: `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || user.email,
                  email: user.email
                }
              });
            }
          } catch (error) {
            console.error('Error fetching physician pricing for AM:', error);
            setAccessModalData({
              products: [],
              catalogs: [],
              accountManager: {
                id: user._id,
                name: `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || user.email,
                email: user.email
              }
            });
          }
        } else {
          setAccessModalData({});
        }
      } else {
        // For other non-admin users, just show basic info
        setAccessModalData({});
      }
    } catch (error: any) {
      console.error('Error fetching user details:', error);
      setModalError(
        userItem.role === 'distributor' 
          ? 'Failed to load account manager details'
          : 'Failed to load clinic details'
      );
    } finally {
      setModalLoading(false);
    }
  };


  const handleRetryLoadUserDetails = () => {
    if (subject) {
      load(subject);
    }
  };

  const close = () => {
    onClose();
    setAccessModalData(null);
    setModalLoading(false);
    setModalError(null);
    setPaymentInstructions('');
    setTeamMembers([]);
    setShowAddTeamMember(false);
    setEditingPaymentInstructions(false);
    // Reset billing config state
    setEditingBillingConfig(false);
    setBillingCreditLimit('');
    setBillingPaymentTerms('immediate');
    setCreditStatus(null);
    setOriginalPaymentInstructions('');
    // Reset reassignment state
    setShowReassignDropdown(false);
    setSearchAM('');
  };

  // Load available account managers for reassignment. Server-side search:
  // /users/distributors is paginated, so client-side filtering only saw
  // page 1. Each query change refetches a fresh page from the backend.
  // A response that arrives after a newer search is dropped (seq guard).
  const loadAccountManagers = async (search: string = '') => {
    const mine = ++amSearchSeq.current;
    try {
      setLoadingAccountManagers(true);
      const result = await api.searchUsersPage({ role: 'distributor', search, pageSize: 50 });
      if (mine !== amSearchSeq.current) return;
      setAvailableAccountManagers(result.items as any);
      setAccountManagersTotal(result.hasMore ? result.total : null);
    } catch (error) {
      if (mine === amSearchSeq.current) console.error('Failed to load account managers:', error);
    } finally {
      if (mine === amSearchSeq.current) setLoadingAccountManagers(false);
    }
  };

  // Handle opening reassignment dropdown
  const handleOpenReassignDropdown = () => {
    setShowReassignDropdown(true);
    loadAccountManagers();
  };


  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '—';
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  };

  // Fetch on open and whenever the subject changes.
  useEffect(() => {
    if (subject?._id) load(subject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject?._id, refreshToken]);

  // /users/distributors is paginated, so the reassign dropdown searches
  // server-side; refetch a fresh page as the query changes.
  useEffect(() => {
    if (!showReassignDropdown) return;
    const handle = setTimeout(() => { loadAccountManagers(searchAM); }, 250);
    return () => clearTimeout(handle);
  }, [searchAM, showReassignDropdown]);

  if (!subject) return null;

  return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{subject.role === 'distributor' ? 'Account Manager Overview' : subject.role === 'supervisor' ? 'Supervisor Overview' : 'Clinic Access'}</h3>
                <p className="text-sm text-gray-500">{subject.role === 'distributor' ? 'Assigned catalogs, payment info, and product pricing' : subject.role === 'supervisor' ? 'Account managers, assigned catalogs, and payment info' : 'Products available, source catalogs, and cost before their own markup'}</p>
              </div>
              <div className="flex items-center space-x-2">
                {/* Move supervisor — admin viewing any AM. An AM with no
                    supervisor today is admin-direct; moving it is also how an
                    admin puts it under a supervisor for the first time. */}
                {user?.role === 'admin' && subject.role === 'distributor' && (
                  <button
                    onClick={() => onMoveSupervisor()}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title={subject.distributorData?.supervisorId
                      ? 'Move this AM under a different supervisor'
                      : 'Place this admin-direct AM under a supervisor'}
                  >
                    {subject.distributorData?.supervisorId ? 'Move to new supervisor' : 'Assign a supervisor'}
                  </button>
                )}
                {/* Impersonate Button - Admin only, non-admin users only */}
                {user?.role === 'admin' && subject.role !== 'admin' && (
                  <button
                    onClick={onImpersonate}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-lg hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    title="Impersonate this user"
                  >
                    <UserCog className="w-4 h-4 mr-2" />
                    Impersonate
                  </button>
                )}
                <button className="p-2 text-gray-400 hover:text-gray-600" onClick={close}>
                  ✕
                </button>
              </div>
            </div>
            <div className="p-5 overflow-auto">
              {/* What this account is waiting on. The directory is organised
                  around exactly this, and opening a row used to drop it. */}
              <UserStateBanner userId={subject._id} />
              {/* Directly under the state, because for a pending approval this
                  is the evidence the decision rests on. */}
              <SignupIntake subject={subject} />
              <NetworkAssignment subject={subject} />
              {/* The ID review lives here rather than in a second modal opened
                  on top of this one. It is about this account, so it belongs in
                  this account's view. Rendered only when there is something to
                  review — it fetches documents, so mounting it for every
                  physician would be a request nobody asked for. */}
              {subject.role === 'physician'
                && subject.physicianData?.idProofingStatus === 'submitted' && (
                <div className="mb-4">
                  <PhysicianIDProofingViewer
                    physicianId={subject._id}
                    isOpen={true}
                    embedded
                    onClose={onClose}
                  />
                </div>
              )}
              {/* Every other clinic: the same documents (license, selfie,
                  signature, NPI registry, credentials) behind a collapsed
                  section, fetched only when an admin opens it. */}
              {subject.role === 'physician'
                && subject.physicianData?.idProofingStatus !== 'submitted'
                && (user?.role === 'admin' || user?.role === 'distributor') && (
                <div className="mb-4 rounded-lg border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowIdProofing(v => !v)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                    aria-expanded={showIdProofing}
                  >
                    <span>
                      ID proofing
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {(subject.physicianData?.idProofingStatus || 'not started').replace(/_/g, ' ')}
                      </span>
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showIdProofing ? 'rotate-180' : ''}`} />
                  </button>
                  {showIdProofing && (
                    <div className="border-t border-gray-200 p-2">
                      <PhysicianIDProofingViewer
                        physicianId={subject._id}
                        isOpen={true}
                        embedded
                        onClose={onClose}
                      />
                    </div>
                  )}
                </div>
              )}
              {modalLoading ? (
                <div data-testid="modal-loading-spinner" className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                  <span className="text-gray-600 font-medium">
                    Loading {subject?.role === 'distributor' ? 'account manager' : subject?.role === 'supervisor' ? 'supervisor' : 'physician'} details...
                  </span>
                  <span className="text-sm text-gray-500 mt-1">
                    {subject?.role === 'distributor'
                      ? 'Fetching catalog and payment information'
                      : subject?.role === 'supervisor'
                      ? 'Fetching account manager and catalog information'
                      : 'Fetching pricing and catalog assignments'
                    }
                  </span>
                </div>
              ) : (
                <div className="space-y-4">
                {/* Contact Information Section - Always show since we have subject */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <UserCheck className="w-4 h-4 mr-2 text-blue-500" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Full Name</div>
                      <div className="text-sm text-gray-900 mt-1">
                        {subject.profile.firstName} {subject.profile.lastName}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</div>
                      <div className="text-sm text-gray-900 mt-1 flex items-center">
                        <Mail className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                        <a href={`mailto:${subject.email}`} className="text-blue-600 hover:underline">
                          {subject.email}
                        </a>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone</div>
                      <div className="text-sm text-gray-900 mt-1 flex items-center">
                        <Phone className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                        <a href={`tel:${subject.profile.phone}`} className="text-blue-600 hover:underline">
                          {subject.profile.phone}
                        </a>
                      </div>
                    </div>
                    {/* Clinic Name for physicians */}
                    {(subject.role === 'physician' || subject.role === 'physician_readonly') && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Clinic / Practice</div>
                        <div className="text-sm text-gray-900 mt-1 flex items-center">
                          <Building2 className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                          {subject.profile?.clinicName || <span className="text-gray-400 italic">Not specified</span>}
                        </div>
                      </div>
                    )}
                    {/* NPI for physicians — admins can edit inline (anytime);
                        everyone else sees it read-only. */}
                    {(subject.role === 'physician' || subject.role === 'physician_readonly') && (
                      user?.role === 'admin' ? (
                        <AdminNpiEditor
                          key={subject._id}
                          physicianId={subject._id}
                          currentNpi={subject.physicianData?.npiNumber}
                        />
                      ) : subject.physicianData?.npiNumber ? (
                        <div>
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">NPI Number</div>
                          <div className="text-sm text-gray-900 mt-1 font-mono">
                            {subject.physicianData.npiNumber}
                          </div>
                        </div>
                      ) : null
                    )}
                    {/* Company for distributors and supervisors */}
                    {(subject.role === 'distributor' || subject.role === 'supervisor') && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Company</div>
                        <div className="text-sm text-gray-900 mt-1 flex items-center">
                          <Building2 className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                          {(subject.role === 'supervisor'
                            ? subject.supervisorData?.companyName
                            : subject.distributorData?.companyName) || <span className="text-gray-400 italic">Not specified</span>}
                        </div>
                      </div>
                    )}
                    {/* Ava assistant — admin-controlled, off by default */}
                    {user?.role === 'admin' && (subject.role === 'distributor' || subject.role === 'supervisor') && (
                      <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ava Assistant</div>
                        <div className="mt-1"><AvaAccessToggle userId={subject._id} enabled={(subject as any).avaEnabled !== false} /></div>
                      </div>
                    )}
                    {/* Address for distributors */}
                    {subject.role === 'distributor' && subject.distributorData?.address && (
                      <div className="md:col-span-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Business Address</div>
                        <div className="text-sm text-gray-900 mt-1">
                          {subject.distributorData.address.street && (
                            <div>{subject.distributorData.address.street}</div>
                          )}
                          {subject.distributorData.address.street2 && (
                            <div>{subject.distributorData.address.street2}</div>
                          )}
                          <div>
                            {[
                              subject.distributorData.address.city,
                              subject.distributorData.address.state,
                              subject.distributorData.address.zipCode
                            ].filter(Boolean).join(', ')}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Address for supervisors */}
                    {subject.role === 'supervisor' && subject.supervisorData?.address && (
                      <div className="md:col-span-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Business Address</div>
                        <div className="text-sm text-gray-900 mt-1">
                          {subject.supervisorData.address.street && (
                            <div>{subject.supervisorData.address.street}</div>
                          )}
                          {subject.supervisorData.address.street2 && (
                            <div>{subject.supervisorData.address.street2}</div>
                          )}
                          <div>
                            {[
                              subject.supervisorData.address.city,
                              subject.supervisorData.address.state,
                              subject.supervisorData.address.zipCode
                            ].filter(Boolean).join(', ')}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Clinic Address for physicians */}
                    {(subject.role === 'physician' || subject.role === 'physician_readonly') && subject.physicianData?.clinicAddress && (
                      <div className="md:col-span-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Clinic Address</div>
                        <div className="text-sm text-gray-900 mt-1">
                          {subject.physicianData.clinicAddress.street && (
                            <div>{subject.physicianData.clinicAddress.street}</div>
                          )}
                          {subject.physicianData.clinicAddress.street2 && (
                            <div>{subject.physicianData.clinicAddress.street2}</div>
                          )}
                          <div>
                            {[
                              subject.physicianData.clinicAddress.city,
                              subject.physicianData.clinicAddress.state,
                              subject.physicianData.clinicAddress.zipCode
                            ].filter(Boolean).join(', ')}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Percent-markup mode (admin only, supervisor/AM only) */}
                    {user?.role === 'admin' && (subject.role === 'supervisor' || subject.role === 'distributor') && (
                      <div className="md:col-span-2 border-t border-gray-200 pt-3">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Percent Markup Mode
                        </div>
                        {(subject as any).percentMarkupEnabled ? (
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Enabled · {(subject as any).percentMarkupPercent}% of every sale
                            </span>
                            <button
                              type="button"
                              disabled={percentMarkupSaving}
                              onClick={async () => {
                                setPercentMarkupSaving(true);
                                setPercentMarkupError(null);
                                setPercentMarkupOffending(null);
                                const res = await api.updatePercentMarkupConfig(subject._id, { enabled: false });
                                setPercentMarkupSaving(false);
                                if (res.success) {
                                  setSelectedUser({ ...subject, percentMarkupEnabled: false } as any);
                                } else {
                                  setPercentMarkupError(res.error || 'Failed to disable');
                                }
                              }}
                              className="text-sm text-red-600 hover:text-red-800 underline"
                            >
                              Disable
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              placeholder="e.g. 20"
                              value={percentMarkupInput}
                              onChange={(e) => setPercentMarkupInput(e.target.value)}
                              className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <span className="text-sm text-gray-600">%</span>
                            <button
                              type="button"
                              disabled={percentMarkupSaving || !percentMarkupInput}
                              onClick={async () => {
                                const pct = parseFloat(percentMarkupInput);
                                if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
                                  setPercentMarkupError('Percent must be between 0 and 100');
                                  return;
                                }
                                setPercentMarkupSaving(true);
                                setPercentMarkupError(null);
                                setPercentMarkupOffending(null);
                                const res: any = await api.updatePercentMarkupConfig(subject._id, { enabled: true, percent: pct });
                                setPercentMarkupSaving(false);
                                if (res.success) {
                                  setSelectedUser({ ...subject, percentMarkupEnabled: true, percentMarkupPercent: pct } as any);
                                  setPercentMarkupInput('');
                                } else {
                                  setPercentMarkupError(res.error || 'Failed to enable');
                                  if (res.offending) setPercentMarkupOffending(res.offending);
                                }
                              }}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                            >
                              Enable percent mode
                            </button>
                          </div>
                        )}
                        {percentMarkupError && (
                          <div className="mt-2 text-xs text-red-600">{percentMarkupError}</div>
                        )}
                        {percentMarkupOffending && percentMarkupOffending.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            <div className="font-medium">Catalogs to clean up first:</div>
                            <ul className="list-disc list-inside">
                              {percentMarkupOffending.map((o: any) => (
                                <li key={o.catalogId}>{o.catalogName} — {o.reasons.length} mismatch(es)</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="mt-1 text-xs text-gray-500">
                          Pass-through pricing — user takes a flat % of each sale, eats into admin's slice. Existing catalogs must already match upstream prices to enable.
                        </div>
                      </div>
                    )}

                    {/* Internal sales-rep mode (admin only, AM/distributor only) */}
                    {user?.role === 'admin' && subject.role === 'distributor' && (
                      <div className="md:col-span-2 border-t border-gray-200 pt-3">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Internal Sales Rep
                        </div>
                        {(subject as any).salesRepEnabled ? (
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Enabled · assigned the sales-rep catalog
                            </span>
                            <button
                              type="button"
                              disabled={salesRepSaving}
                              onClick={async () => {
                                setSalesRepSaving(true);
                                setSalesRepError(null);
                                const res = await api.updateSalesRepConfig(subject._id, { enabled: false });
                                setSalesRepSaving(false);
                                if (res.success) {
                                  setSelectedUser({ ...subject, salesRepEnabled: false } as any);
                                } else {
                                  setSalesRepError(res.error || 'Failed to disable');
                                }
                              }}
                              className="text-sm text-red-600 hover:text-red-800 underline"
                            >
                              Disable
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={salesRepSaving || (subject as any).percentMarkupEnabled || !!subject.distributorData?.supervisorId}
                            onClick={async () => {
                              setSalesRepSaving(true);
                              setSalesRepError(null);
                              const res: any = await api.updateSalesRepConfig(subject._id, { enabled: true });
                              setSalesRepSaving(false);
                              if (res.success) {
                                setSelectedUser({ ...subject, salesRepEnabled: true } as any);
                                if (res.data?.warning) setSalesRepError(res.data.warning);
                              } else {
                                setSalesRepError(res.error || 'Failed to enable');
                              }
                            }}
                            className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                          >
                            Enable sales-rep mode
                          </button>
                        )}
                        {salesRepError && (
                          <div className="mt-2 text-xs text-amber-700">{salesRepError}</div>
                        )}
                        <div className="mt-1 text-xs text-gray-500">
                          {(subject as any).percentMarkupEnabled
                            ? 'Disable commission mode first — the two are mutually exclusive.'
                            : subject.distributorData?.supervisorId
                              ? 'Sales reps must report directly to admin. Remove this AM’s supervisor first.'
                              : 'Assigns the special sales-rep catalog. The rep sells from it at the list price and may step the price down in preset tiers, giving up commission as they discount.'}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</div>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          subject.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : subject.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {subject.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Member Since</div>
                      <div className="text-sm text-gray-900 mt-1">
                        {formatDateUtil(subject.createdAt, { month: 'long' })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Members Section - Supervisors only, Admin only */}
                {user?.role === 'admin' && subject.role === 'supervisor' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                        <Users className="w-4 h-4 mr-2 text-purple-500" />
                        Team Members
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          (additional logins for this supervisor account)
                        </span>
                      </h4>
                      <button
                        onClick={() => { setShowAddTeamMember(!showAddTeamMember); setNewTeamMember({ email: '', firstName: '', lastName: '' }); }}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add
                      </button>
                    </div>

                    {/* Add Team Member Form */}
                    {showAddTeamMember && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="First name"
                            value={newTeamMember.firstName}
                            onChange={(e) => setNewTeamMember({ ...newTeamMember, firstName: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <input
                            type="text"
                            placeholder="Last name"
                            value={newTeamMember.lastName}
                            onChange={(e) => setNewTeamMember({ ...newTeamMember, lastName: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={newTeamMember.email}
                            onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 md:col-span-2"
                          />
                        </div>
                        <div className="flex justify-end space-x-2 mt-3">
                          <button
                            onClick={() => setShowAddTeamMember(false)}
                            className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                          <button
                            disabled={addTeamMemberLoading || !newTeamMember.email || !newTeamMember.firstName || !newTeamMember.lastName}
                            onClick={async () => {
                              setAddTeamMemberLoading(true);
                              const res = await api.createTeamMember({
                                ...newTeamMember,
                                supervisorUserId: subject._id
                              });
                              setAddTeamMemberLoading(false);
                              if (res.success) {
                                toast.success('Team member created — they will receive an email to set their password');
                                setShowAddTeamMember(false);
                                setNewTeamMember({ email: '', firstName: '', lastName: '' });
                                // Refresh team members
                                const tmRes = await api.getTeamMembers(subject._id);
                                if (tmRes.success) setTeamMembers(tmRes.data || []);
                              } else {
                                toast.error(res.error || 'Failed to create team member');
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {addTeamMemberLoading ? 'Creating...' : 'Create'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Team Members List */}
                    {teamMembersLoading ? (
                      <div className="text-sm text-gray-500 py-4 text-center">Loading team members...</div>
                    ) : teamMembers.length === 0 ? (
                      <div className="text-sm text-gray-400 py-4 text-center italic">No team members yet</div>
                    ) : (
                      <div className="space-y-2">
                        {teamMembers.map((tm: any) => (
                          <div key={tm._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-xs font-semibold text-purple-700">
                                  {tm.profile?.firstName?.[0]}{tm.profile?.lastName?.[0]}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {tm.profile?.firstName} {tm.profile?.lastName}
                                </div>
                                <div className="text-xs text-gray-500">{tm.email}</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                tm.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {tm.status}
                              </span>
                              {tm.status === 'active' ? (
                                <button
                                  onClick={async () => {
                                    const res = await api.deleteTeamMember(tm._id);
                                    if (res.success) {
                                      toast.success('Team member suspended');
                                      const tmRes = await api.getTeamMembers(subject._id);
                                      if (tmRes.success) setTeamMembers(tmRes.data || []);
                                    } else {
                                      toast.error(res.error || 'Failed to suspend');
                                    }
                                  }}
                                  className="text-xs text-red-600 hover:text-red-700"
                                  title="Suspend team member"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={async () => {
                                    const res = await api.updateTeamMember(tm._id, { status: 'active' });
                                    if (res.success) {
                                      toast.success('Team member reactivated');
                                      const tmRes = await api.getTeamMembers(subject._id);
                                      if (tmRes.success) setTeamMembers(tmRes.data || []);
                                    } else {
                                      toast.error(res.error || 'Failed to reactivate');
                                    }
                                  }}
                                  className="text-xs text-green-600 hover:text-green-700"
                                  title="Reactivate team member"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}



                {/* Commission Overrides Section - Physicians only, Admin only */}
                {user?.role === 'admin' && (subject.role === 'physician' || subject.role === 'physician_readonly') && (
                  <CommissionOverridesPanel
                    scope="account"
                    targetId={subject._id}
                    targetName={subject.profile?.clinicName || `${subject.profile?.firstName || ''} ${subject.profile?.lastName || ''}`.trim()}
                    canManage={user?.role === 'admin'}
                  />
                )}

                {/* Commission Overrides Section - Supervisors only, Admin only.
                    A supervisor tag pays a lead-source partner out of the house
                    margin on everything beneath the supervisor, so it belongs on
                    the supervisor rather than on each clinic. */}
                {user?.role === 'admin' && subject.role === 'supervisor' && (
                  <CommissionOverridesPanel
                    scope="supervisor"
                    targetId={subject._id}
                    targetName={`${subject.profile?.firstName || ''} ${subject.profile?.lastName || ''}`.trim() || subject.email}
                    canManage={user?.role === 'admin'}
                  />
                )}

                {/* Negotiated Deals Section - Physicians only, Admin only.
                    Sits beside commission overrides: same mental model, both are
                    special arrangements attached to this account. */}
                {user?.role === 'admin' && (subject.role === 'physician' || subject.role === 'physician_readonly') && (
                  <div className="mb-4">
                    <ClinicDealsPanel
                      accountId={subject._id}
                      accountName={subject.profile?.clinicName || `${subject.profile?.firstName || ''} ${subject.profile?.lastName || ''}`.trim()}
                      canManage={user?.role === 'admin'}
                    />
                  </div>
                )}

                {/* Billing Configuration Section - Physicians only, Admin only */}
                {user?.role === 'admin' && (subject.role === 'physician' || subject.role === 'physician_readonly') && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <CreditCard className="w-5 h-5 text-blue-600 mr-2" />
                        <h4 className="text-sm font-semibold text-gray-900">Billing Configuration</h4>
                      </div>
                      {!editingBillingConfig ? (
                        <button
                          onClick={() => setEditingBillingConfig(true)}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleCancelBillingConfig}
                            disabled={billingConfigLoading}
                            className="text-sm text-gray-600 hover:text-gray-700 flex items-center"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveBillingConfig}
                            disabled={billingConfigLoading}
                            className="text-sm text-green-600 hover:text-green-700 flex items-center"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            {billingConfigLoading ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Credit Limit */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                          Credit Limit
                        </label>
                        {editingBillingConfig ? (
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={billingCreditLimit}
                              onChange={(e) => setBillingCreditLimit(e.target.value)}
                              placeholder="Unlimited"
                              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {subject.physicianData?.creditLimit != null
                              ? `$${subject.physicianData.creditLimit.toLocaleString()}`
                              : <span className="text-gray-400 italic">Unlimited</span>
                            }
                          </div>
                        )}
                      </div>

                      {/* Payment Terms */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                          Payment Terms
                        </label>
                        {editingBillingConfig ? (
                          <select
                            value={billingPaymentTerms}
                            onChange={(e) => setBillingPaymentTerms(e.target.value as 'immediate' | 'net7' | 'net15' | 'net30')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="immediate">Immediate</option>
                            <option value="net7">Net 7</option>
                            <option value="net15">Net 15</option>
                            <option value="net30">Net 30</option>
                          </select>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {subject.physicianData?.paymentTerms === 'immediate' ? 'Immediate' :
                             subject.physicianData?.paymentTerms === 'net7' ? 'Net 7' :
                             subject.physicianData?.paymentTerms === 'net15' ? 'Net 15' :
                             'Net 30'}
                          </div>
                        )}
                      </div>

                      {/* AR (Accounts Receivable) Enabled */}
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                          Accounts Receivable (AR)
                        </label>
                        {editingBillingConfig ? (
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setBillingArEnabled(!billingArEnabled)}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                billingArEnabled ? 'bg-blue-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  billingArEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            <span className="text-sm text-gray-600">
                              {billingArEnabled ? 'Enabled - Can ship before payment (within credit limit)' : 'Disabled - Must pay before shipping'}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              subject.physicianData?.arEnabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {subject.physicianData?.arEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {subject.physicianData?.arEnabled
                                ? 'Can ship before payment'
                                : 'Must pay before shipping'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Patient Pay Enabled */}
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                          Patient Pay (Online Payments)
                        </label>
                        {editingBillingConfig ? (
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setBillingPatientPayEnabled(!billingPatientPayEnabled)}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                billingPatientPayEnabled ? 'bg-blue-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  billingPatientPayEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            <span className="text-sm text-gray-600">
                              {billingPatientPayEnabled ? 'Enabled - Patients can pay online for their orders' : 'Disabled - No patient online payment option'}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              subject.physicianData?.patientPayEnabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {subject.physicianData?.patientPayEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {subject.physicianData?.patientPayEnabled
                                ? 'Patients can pay online'
                                : 'No patient online payment'}
                            </span>
                          </div>
                        )}
                      </div>


                      {/* Credit Status (read-only) */}
                      {creditStatus && (
                        <>
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              Outstanding Balance
                            </div>
                            <div className={`text-sm font-medium ${creditStatus.isOverLimit ? 'text-red-600' : 'text-gray-900'}`}>
                              ${creditStatus.outstandingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {creditStatus.isOverLimit && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Over Limit
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              Available Credit
                            </div>
                            <div className="text-sm text-gray-900">
                              {creditStatus.availableCredit != null
                                ? `$${creditStatus.availableCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : <span className="text-gray-400 italic">Unlimited</span>
                              }
                            </div>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                              Unpaid Invoices
                            </div>
                            <div className="text-sm text-gray-900">
                              {creditStatus.invoiceCount}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                      {/* Payout instructions, folded in rather than floating as
                          their own card. This is money-out detail and belongs
                          beside credit terms and AR, not in a separate blue
                          panel competing with them.

                          Read-only here because the account owner sets it in
                          their own Settings; admins see it, they do not author
                          it. Supervisors keep a standalone copy below, since
                          this whole section is physicians-only. */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Payout Instructions
                        </div>
                        {subject.physicianData?.paymentInstructions ? (
                          <div className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 p-3 rounded border border-gray-200">
                            {subject.physicianData.paymentInstructions}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 italic">
                            None provided — the clinic sets these in their Settings.
                          </div>
                        )}
                      </div>
                  </div>
                )}

                {/* Supervisors only now — the clinic case moved inside Billing
                    Configuration above, which is physicians-only, so this still
                    has to exist for everyone it does not cover. */}
                {user?.role === 'admin' && subject.role === 'supervisor' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center mb-3">
                      <CreditCard className="w-5 h-5 text-blue-600 mr-2" />
                      <h4 className="text-sm font-semibold text-gray-900">Payout Instructions</h4>
                    </div>
                    {(() => {
                      const instructions = subject.supervisorData?.paymentInstructions;
                      return instructions ? (
                        <div className="text-sm text-blue-800 whitespace-pre-wrap bg-white p-3 rounded border">
                          {instructions}
                        </div>
                      ) : (
                        <div className="text-sm text-blue-600 italic">
                          No payout instructions provided. The supervisor sets these in their Settings.
                        </div>
                      );
                    })()}
                  </div>
                )}

                {modalError ? (
                <div data-testid="modal-error-state" className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Details</h3>
                  <p className="text-gray-600 mb-4">
                    {modalError?.includes('timeout') || modalError?.includes('Request timeout')
                      ? 'The request took too long to complete. This may be due to network issues or server load.'
                      : modalError?.includes('404') || modalError?.includes('Not Found')
                      ? 'The requested data could not be found. This user may need catalog assignments.'
                      : modalError || 'There was an issue loading the details. This may be a temporary network problem.'
                    }
                  </p>
                  
                  {/* Troubleshooting tips */}
                  <div className="max-w-md mx-auto mb-6">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
                      <h4 className="text-sm font-medium text-gray-800 mb-2">Troubleshooting:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {subject?.role === 'physician' ? (
                          <>
                            <li>• Check if the clinic has assigned catalogs</li>
                            <li>• Verify account manager relationship</li>
                            <li>• Ensure pricing data is configured</li>
                          </>
                        ) : (
                          <>
                            <li>• Check if catalog templates exist</li>
                            <li>• Verify payment settings configuration</li>
                            <li>• Ensure distributor account is active</li>
                          </>
                        )}
                        <li>• Try refreshing in a few moments</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={handleRetryLoadUserDetails}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Retry
                    </button>
                    <button
                      onClick={close}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : !accessModalData ? (
                <div className="text-sm text-gray-500">Loading additional details...</div>
              ) : (
                <>
                  {subject.role === 'distributor' && (
                    <div className="space-y-4">
                      {/* Assigned Catalogs Section */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Assigned Catalogs</h4>
                        {accessModalData?.assignedCatalogs && accessModalData.assignedCatalogs.length > 0 ? (
                          <div className="space-y-2">
                            {accessModalData.assignedCatalogs
                              .filter((catalog: any, index: number, array: any[]) => 
                                // Deduplicate by catalog id - keep only first occurrence
                                array.findIndex((item: any) => item.id === catalog.id) === index
                              )
                              .map((catalog: any, index: number) => (
                                <div key={`${catalog.id}-${index}`} className="bg-white p-3 rounded border">
                                <div className="font-medium text-gray-900">{catalog.name}</div>
                                {catalog.description && (
                                  <div className="text-sm text-gray-600 mt-1">{catalog.description}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-600 italic">
                            No catalogs assigned to this account manager.
                          </div>
                        )}
                      </div>
                      
                      {/* Payment Instructions Section */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <CreditCard className="h-4 w-4 text-blue-600" />
                            <h4 className="text-sm font-medium text-blue-900">Payment Instructions</h4>
                          </div>
                          {user?.role === 'admin' && !editingPaymentInstructions && (
                            <button
                              onClick={handleEditPaymentInstructions}
                              className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                              title="Edit payment instructions"
                            >
                              <Edit3 className="h-3 w-3" />
                              <span>Edit</span>
                            </button>
                          )}
                        </div>
                        {editingPaymentInstructions ? (
                          <div className="space-y-3">
                            <textarea
                              value={paymentInstructions}
                              onChange={(e) => setPaymentInstructions(e.target.value)}
                              rows={6}
                              className="w-full px-3 py-2 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter payment instructions including wire transfer details, bank information, preferred payment methods, etc."
                            />
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={handleCancelEditPaymentInstructions}
                                disabled={savingPaymentInstructions}
                                className="flex items-center space-x-1 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
                              >
                                <X className="h-3 w-3" />
                                <span>Cancel</span>
                              </button>
                              <button
                                onClick={handleSavePaymentInstructions}
                                disabled={savingPaymentInstructions}
                                className="flex items-center space-x-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                              >
                                {savingPaymentInstructions ? (
                                  <>
                                    <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />
                                    <span>Saving...</span>
                                  </>
                                ) : (
                                  <>
                                    <Save className="h-3 w-3" />
                                    <span>Save</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {paymentInstructions ? (
                              <div className="text-sm text-blue-800 whitespace-pre-wrap bg-white p-3 rounded border">
                                {paymentInstructions}
                              </div>
                            ) : (
                              <div className="text-sm text-blue-600 italic">
                                No payment instructions provided by this account manager.
                              </div>
                            )}
                          </>
                        )}
                      </div>

                    </div>
                  )}
                  {subject.role === 'supervisor' && accessModalData?.catalogs && (
                    <div className="text-sm text-gray-700">
                      <div className="font-medium mb-2">Assigned Catalogs</div>
                      {accessModalData.catalogs.length > 0 ? (
                        <ul className="list-disc pl-6">
                          {accessModalData.catalogs
                            .filter((c: any, index: number, array: any[]) =>
                              array.findIndex((item: any) => item.catalogId === c.catalogId) === index
                            )
                            .map((c: any, index: number) => (
                              <li key={`${c.catalogId}-${index}`}>{c.catalogName}</li>
                            ))}
                        </ul>
                      ) : (
                        <div className="text-gray-600 italic">No catalogs assigned to this supervisor.</div>
                      )}
                    </div>
                  )}
                  {subject.role === 'supervisor' && (
                    <div className="mt-4">
                      <PartnerApiSection supervisorUserId={subject._id} isAdmin={user?.role === 'admin'} />
                    </div>
                  )}
                </>
              )}
              </div>
              )}


              {/* One hierarchy, not three. The AM's name, their catalogs and
                  the reassign control were each rendered separately above this
                  tree, which then showed the same AM and the same catalogs
                  again. Reassign now sits on the AM node it acts on, the
                  product list sits under the catalogs it prices, and every
                  level links through to its own account. */}
              <UserChainPanel
                below={
                  <>
                  {/* Supervisor: Account Managers (collapsible) */}
                  {subject.role === 'supervisor' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg">
                      <button
                        onClick={() => setShowSupervisorAMs(!showSupervisorAMs)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <h4 className="text-sm font-medium text-gray-900">
                          Account Managers ({supervisorAMs.length})
                        </h4>
                        {showSupervisorAMs ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                      </button>
                      {showSupervisorAMs && (
                        <div className="px-4 pb-4">
                          {supervisorAMs.length > 0 ? (
                            <div className="border rounded-lg overflow-hidden">
                              <table className="min-w-full text-sm">
                                <thead className="bg-white">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Company</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {supervisorAMs.map((am: any) => (
                                    <tr key={am._id} className="border-t">
                                      <td className="px-3 py-2">{am.profile?.firstName} {am.profile?.lastName}</td>
                                      <td className="px-3 py-2 text-gray-600">{am.distributorData?.companyName || '—'}</td>
                                      <td className="px-3 py-2 text-gray-600">{am.email}</td>
                                      <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                          am.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                          {am.status}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600 italic">No account managers under this supervisor.</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Supervisor: Clinics (collapsible) */}
                  {subject.role === 'supervisor' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg">
                      <button
                        onClick={() => setShowSupervisorPhysicians(!showSupervisorPhysicians)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <h4 className="text-sm font-medium text-gray-900">
                          Clinics ({supervisorPhysicians.length})
                        </h4>
                        {showSupervisorPhysicians ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                      </button>
                      {showSupervisorPhysicians && (
                        <div className="px-4 pb-4">
                          {supervisorPhysicians.length > 0 ? (
                            <div className="border rounded-lg overflow-hidden">
                              <table className="min-w-full text-sm">
                                <thead className="bg-white">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Clinic</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Account Manager</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {supervisorPhysicians.map((phys: any) => {
                                    const am = supervisorAMs.find((a: any) => a._id === phys.physicianData?.distributorId?.toString() || a._id === phys.physicianData?.distributorId);
                                    return (
                                      <tr key={phys._id} className="border-t">
                                        <td className="px-3 py-2">{phys.profile?.firstName} {phys.profile?.lastName}</td>
                                        <td className="px-3 py-2 text-gray-600">{phys.profile?.clinicName || phys.physicianData?.clinicName || '—'}</td>
                                        <td className="px-3 py-2 text-gray-600">{am ? `${am.profile?.firstName} ${am.profile?.lastName}` : '—'}</td>
                                        <td className="px-3 py-2">
                                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            phys.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                          }`}>
                                            {phys.status}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600 italic">No physicians under this supervisor's account managers.</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* An AM's own clinics, in the same hierarchy block as
                      everything above them. */}
                  {subject.role === 'distributor' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg">
                            <button
                              onClick={() => setShowPhysicians(!showPhysicians)}
                              className="w-full flex items-center justify-between p-4 text-left"
                            >
                              <h4 className="text-sm font-medium text-gray-900">
                                Clinics ({amPhysicians.length})
                              </h4>
                              {showPhysicians ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                            </button>
                            {showPhysicians && (
                              <div className="px-4 pb-4">
                                {amPhysicians.length > 0 ? (
                                  <div className="border rounded-lg overflow-hidden">
                                    <table className="min-w-full text-sm">
                                      <thead className="bg-white">
                                        <tr>
                                          <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-700">Clinic</th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {amPhysicians.map((phys: any) => (
                                          <tr key={phys._id} className="border-t">
                                            <td className="px-3 py-2">{phys.profile?.firstName} {phys.profile?.lastName}</td>
                                            <td className="px-3 py-2 text-gray-600">{phys.profile?.clinicName || phys.physicianData?.clinicName || '—'}</td>
                                            <td className="px-3 py-2 text-gray-600">{phys.email}</td>
                                            <td className="px-3 py-2">
                                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                phys.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                              }`}>
                                                {phys.status}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-600 italic">No physicians assigned to this account manager.</div>
                                )}
                              </div>
                            )}
                          </div>
                  )}
                  </>
                }
                userId={subject._id}
                onOpenUser={onOpenUser}
                amActions={
                  <>
          {user?.role === 'admin' && (
                                    <button
                                      data-testid="reassign-account-manager-btn"
                                      onClick={handleOpenReassignDropdown}
                                      className="text-xs px-2 py-1 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      Reassign Account Manager
                                    </button>
                                  )}
          {/* Account Manager Reassignment Dropdown */}
                                {showReassignDropdown && (
                                  <div className="mb-4 p-3 border border-blue-200 rounded-lg bg-blue-50">
                                    <div className="text-sm font-medium text-blue-900 mb-2">Select New Account Manager</div>
                                    <div className="space-y-2">
                                      <input
                                        type="text"
                                        placeholder="Search account managers..."
                                        value={searchAM}
                                        onChange={(e) => setSearchAM(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <div data-testid="account-manager-dropdown" className="max-h-32 overflow-y-auto">
                                        {loadingAccountManagers ? (
                                          <div className="text-sm text-gray-500 p-2">Loading account managers...</div>
                                        ) : (
                                          (() => {
                                            const list = (Array.isArray(availableAccountManagers) ? availableAccountManagers : [])
                                              .filter(am => am._id !== accessModalData?.accountManager?.id);
                                            if (list.length === 0) {
                                              return <div className="text-sm text-gray-500 p-2">No account managers found</div>;
                                            }
                                            return <>
                                              {list.map(am => (
                                                <div
                                                  key={am._id}
                                                  onClick={() => onSelectNewAM(am)}
                                                  className="p-2 text-sm cursor-pointer hover:bg-blue-100 rounded border-b border-blue-200 last:border-b-0"
                                                >
                                                  {am.distributorData?.companyName || 'No Company'} - {am.profile.firstName} {am.profile.lastName}
                                                </div>
                                              ))}
                                              {accountManagersTotal !== null && (
                                                <div className="p-2 text-xs text-gray-500">
                                                  Showing {availableAccountManagers.length} of {accountManagersTotal}. Type to narrow the search.
                                                </div>
                                              )}
                                            </>;
                                          })()
                                        )}
                                      </div>
                                      <button
                                        onClick={() => setShowReassignDropdown(false)}
                                        className="text-xs text-gray-600 hover:text-gray-800"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                  </>
                }
                footer={
                  /* Guarded: a prop is evaluated on every render, including
                     before the details fetch returns. Inside the old JSX this
                     sat behind an `accessModalData` check; as a prop it read
                     `.products` off null and took the modal down. */
                  !accessModalData ? null : (
                  <>
                            {/* Products Section (collapsible, default collapsed) */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg">
                              <button
                                onClick={() => setShowProducts(!showProducts)}
                                className="w-full flex items-center justify-between p-4 text-left"
                              >
                                <h4 className="text-sm font-medium text-gray-900">
                                  Products ({(accessModalData?.products || []).length})
                                </h4>
                                {showProducts ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                              </button>
                              {showProducts && (
                                <div className="px-4 pb-4">
                                  {(accessModalData?.products || []).length === 0 ? (
                                    <div data-testid="empty-pricing-state" className="text-center py-8">
                                      <Package className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                      <p className="text-sm text-gray-600">
                                        No pricing data available. This {subject?.role === 'distributor' ? 'account manager' : subject?.role === 'supervisor' ? 'supervisor' : 'physician'} may not have any catalogs assigned.
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="border rounded-lg overflow-hidden">
                                      <table className="min-w-full text-sm">
                                        <thead className="bg-white">
                                          <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">Product</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700">Source Catalog</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-700">
                                              {subject?.role === 'distributor' ? 'AM Cost' : subject?.role === 'supervisor' ? 'Supervisor Cost' : 'Clinic Price'}
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(accessModalData?.products || []).map((p: any) => (
                                            <tr key={p._id} className="border-t">
                                              <td className="px-3 py-2">{p.displayName || `${p.drugName} ${p.concentration} ${p.format} ${p.size}`}</td>
                                              <td className="px-3 py-2 text-gray-600">
                                                {p.source?.catalogName || (p.sources && p.sources.map((s: any) => s.catalogName).join(', ')) || '—'}
                                              </td>
                                              <td className="px-3 py-2 text-right font-medium">{formatCurrency(p.calculatedPrice?.finalPrice ?? p.basePrice)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                  </>
                  )
                }
              />

              {/* Always last, whatever the role: reference, not the reason
                  anyone opened this modal. */}
              {/* Feature flags, collapsed.
                  These lived in Billing Configuration because that is where the
                  save lives, not because they are billing: a storefront, a
                  shipping default, two programmes and an account type. Mixed in,
                  they buried credit limit and payment terms under six toggles
                  most reviewers never touch. Grouped and closed by default, on
                  the SAME Edit/Save — one save path, so the two halves cannot
                  drift apart. */}
              <div className="border border-gray-200 rounded-lg mt-4">
                <button
                  onClick={() => setFlagsOpen(o => !o)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                >
                  <span className="flex items-center font-medium text-gray-900">
                    <Settings className="w-5 h-5 mr-2 text-gray-500" />
                    Feature flags
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      storefront, shipping, programmes, account type
                    </span>
                  </span>
                  {flagsOpen
                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                    : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>

                {flagsOpen && (
                  <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                    <div className="flex justify-end mb-3">
                      {!editingBillingConfig ? (
                        <button
                          onClick={() => setEditingBillingConfig(true)}
                          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Edit3 className="w-4 h-4 mr-1" /> Edit
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveBillingConfig}
                            disabled={billingConfigLoading}
                            className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            {billingConfigLoading ? 'Saving\u2026' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelBillingConfig}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Direct to Patient Storefront */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    Direct to Patient Storefront
                  </label>
                  {editingBillingConfig ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setBillingDtpEnabled(!billingDtpEnabled)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          billingDtpEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            billingDtpEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">
                        {billingDtpEnabled ? 'Enabled - Patients can order directly' : 'Disabled'}
                      </span>
                    </div>
                  ) : (() => {
                    // DTP defaults on — only explicit `false` means opted out
                    const dtpOn = subject.physicianData?.directToPatientEnabled !== false;
                    return (
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          dtpOn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {dtpOn ? 'Enabled' : 'Disabled'}
                        </span>
                        {dtpOn && (
                          <span className="text-xs text-blue-600 font-mono">
                            /dtp/{subject._id}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Ava assistant — admin-controlled, off by default. Saves on
                    its own (not with the billing form). */}
                {user?.role === 'admin' && (
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                      Ava Assistant
                    </label>
                    <div className="flex items-center gap-3">
                      <AvaAccessToggle userId={subject._id} enabled={(subject as any).avaEnabled !== false} />
                      <span className="text-sm text-gray-600">Saves immediately</span>
                    </div>
                  </div>
                )}

                {/* DTP signed-link auto-import (admin provisions the per-clinic HMAC secret) */}
                <DtpAutoImportSection physicianId={subject._id} />

                {/* Telemed network the clinic uses for prescribing — visible
                    only to admins with the canManageTelemedNetworks bit. */}
                {user?.role === 'admin' && (user as any)?.adminData?.canManageTelemedNetworks === true && (
                  <TelemedNetworkSection
                    physicianId={subject._id}
                    currentNetworkId={(subject.physicianData as any)?.telemedNetworkId}
                    currentTelemedOnly={(subject.physicianData as any)?.telemedOnly}
                    requestedNetwork={(subject.physicianData as any)?.telemedNetworkRequested}
                    currentUseNetworkForUncoveredStates={(subject.physicianData as any)?.useNetworkForUncoveredStates}
                    networkGapRequested={(subject.physicianData as any)?.networkGapRequested}
                    canEdit={true}
                  />
                )}

                {/* Facility Only (Ship to Clinic) */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    Facility Only (Ship to Clinic)
                  </label>
                  {editingBillingConfig ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setBillingFacilityOnly(!billingFacilityOnly)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          billingFacilityOnly ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            billingFacilityOnly ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">
                        {billingFacilityOnly ? 'Enabled - All orders ship to clinic address' : 'Disabled - Orders ship to patient address by default'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        subject.physicianData?.facilityOnly
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {subject.physicianData?.facilityOnly ? 'Enabled' : 'Disabled'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {subject.physicianData?.facilityOnly
                          ? 'All orders ship to clinic'
                          : 'Orders ship to patient by default'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Custom Domain</label>
                  {editingBillingConfig ? (
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <button type="button" role="switch" aria-label="Custom Domain" aria-checked={billingCustomDomainEnabled}
                        onClick={() => setBillingCustomDomainEnabled(!billingCustomDomainEnabled)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${billingCustomDomainEnabled ? 'bg-blue-600' : 'bg-gray-200'}`}>
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${billingCustomDomainEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                      <span>Storefront and patient portal on the clinic’s own subdomain</span>
                    </div>
                  ) : (
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${subject.physicianData?.customDomainEnabled === true ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {subject.physicianData?.customDomainEnabled === true ? 'Enabled' : 'Disabled'}
                    </span>
                  )}
                  <p className="text-xs text-gray-500 mt-2">New offers start at $166/month with 30 days of overdue grace. The clinic owner sees the price and activates the domain before billing starts.</p>
                </div>

                {/* DTP Membership Program */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    DTP Membership Program
                  </label>
                  {editingBillingConfig ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setBillingMembershipEnabled(!billingMembershipEnabled)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          billingMembershipEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            billingMembershipEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">
                        {billingMembershipEnabled
                          ? 'Enabled - Recurring 1/2/4-peptide tiers ($200/$300/$400 every 45 days)'
                          : 'Disabled'}
                      </span>
                    </div>
                  ) : (() => {
                    const memOn = (subject.physicianData as any)?.membershipEnabled === true;
                    return (
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          memOn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {memOn ? 'Enabled' : 'Disabled'}
                        </span>
                        {memOn && (
                          <span className="text-xs text-blue-600 font-mono">
                            /dtp/{subject._id}/membership
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Volume Rewards Program */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    Volume Rewards Program
                  </label>
                  {editingBillingConfig ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setBillingVolumeRewardsEnabled(!billingVolumeRewardsEnabled)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          billingVolumeRewardsEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            billingVolumeRewardsEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">
                        {billingVolumeRewardsEnabled
                          ? 'Enabled — Weekly status tier based on prior week paid volume'
                          : 'Disabled'}
                      </span>
                    </div>
                  ) : (() => {
                    const vrOn = (subject.physicianData as any)?.volumeRewardsEnabled === true;
                    return (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        vrOn ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {vrOn ? 'Enabled' : 'Disabled'}
                      </span>
                    );
                  })()}
                </div>

                {/* Pharmacy account type.
                    Sits with the other flags because that is where admins
                    look, but it is a heavier switch than the rest: it
                    changes what the account IS, not what it may do. The
                    model requires a pharmacy to carry its own NPI and
                    forbids it being a vet, so a bad combination comes back
                    as a 400 with the model's own message. */}
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">
                    Pharmacy account
                  </label>
                  {editingBillingConfig ? (
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => setBillingIsPharmacy(!billingIsPharmacy)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          billingIsPharmacy ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            billingIsPharmacy ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">
                        {billingIsPharmacy
                          ? 'Enabled — can transfer in outside doctors\u2019 scripts. Requires its own NPI; cannot also be a vet clinic.'
                          : 'Disabled'}
                      </span>
                    </div>
                  ) : (() => {
                    const rxOn = (subject.physicianData as any)?.isPharmacy === true;
                    return (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rxOn ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {rxOn ? 'Pharmacy — Rx transfers enabled' : 'Not a pharmacy'}
                      </span>
                    );
                  })()}
                </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 text-right">
              <button className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700" onClick={close}>Close</button>
            </div>
          </div>
        </div>
  );
}

export default UserAccessModal;
